import {
    Binding,
    ComponentRef,
    Directive,
    effect,
    EnvironmentInjector,
    inject,
    input,
    inputBinding,
    isSignal,
    isWritableSignal,
    outputBinding,
    Renderer2,
    twoWayBinding,
    Type,
    ViewContainerRef,
} from "@angular/core";
import { InputsOf, ModelsOf, OutputsOf } from "../pojo/Util";

@Directive({ selector: "[render-component]" })
export class RenderComponentDirective<Component extends Type<unknown>> {
    readonly component = input.required<Component>({ alias: "render-component" });
    readonly inputs = input<InputsOf<Component> | undefined>(undefined);
    readonly models = input<ModelsOf<Component> | undefined>(undefined);
    readonly outputs = input<OutputsOf<Component> | undefined>(undefined);
    readonly events = input<{ [eventType: string]: (event: any) => void } | undefined>(undefined);

    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly environmentInjector = inject(EnvironmentInjector);
    private readonly renderer = inject(Renderer2);

    private componentRef: ComponentRef<unknown> | null = null;

    constructor() {
        effect((onCleanup) => {
            const componentType = this.component();
            const inputs = this.inputs() ?? {};
            const models = this.models() ?? {};
            const outputs = this.outputs() ?? {};
            const events = this.events() ?? {};

            const bindings: Binding[] = [];
            for (const [input, value] of Object.entries(inputs ?? {})) {
                if (isSignal(value)) bindings.push(inputBinding(input, value));
                else bindings.push(inputBinding(input, () => value));
            }
            for (const [input, value] of Object.entries(models ?? {})) {
                if (isWritableSignal(value)) bindings.push(twoWayBinding(input, value));
                else console.error(`Invalid twoWayInput: "${input}" is not a writable signal.`);
            }
            for (const [output, handler] of Object.entries(outputs ?? {})) {
                if (typeof handler !== "function") console.error(`Invalid output: "${output}" is not a function.`);
                else bindings.push(outputBinding(output, (event) => handler(event)));
            }

            this.viewContainerRef.clear();
            this.componentRef = this.viewContainerRef.createComponent(componentType, {
                bindings,
                environmentInjector: this.environmentInjector,
            });

            // Apply event listeners
            const eventListeners: (() => void)[] = [];
            for (const [eventType, handler] of Object.entries(events ?? {})) {
                eventListeners.push(
                    this.renderer.listen(this.componentRef.location.nativeElement, eventType, (event: unknown) =>
                        handler(event),
                    ),
                );
            }

            onCleanup(() => {
                for (const stop of eventListeners) stop();
                this.componentRef?.destroy();
                this.componentRef = null;
                this.viewContainerRef.clear();
            });
        });
    }
}
